import { alpha, PaletteOptions } from '@mui/material';

import { capitalDesignTokens } from './capital_design_tokens';

// tslint:disable:interface-name
declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    secondary: string;
  }

  interface Palette {
    /**
     * Rest-state border of outlined text inputs: `TextField`, `Select` and the `Autocomplete`
     * input, including the naked multiline variant. Meets the WCAG 2.1 AA 1.4.11 non-text
     * contrast target of 3:1 against `background.paper`, `background.default` and
     * `background.secondary` in both modes. Use `divider` for decorative separators, which are
     * exempt from that rule and deliberately lighter.
     *
     * Checkbox, Radio and Switch draw their unchecked border with `divider`, not this colour.
     */
    outline: string;
    primaryShadowed?: Palette['primary'];
    secondaryShadowed?: Palette['primary'];
    errorShadowed?: Palette['primary'];
    warningShadowed?: Palette['primary'];
    successShadowed?: Palette['primary'];
    infoShadowed: Palette['primary'];
    // Custom chip colors
    inProgress?: Palette['primary'];
    indigo?: Palette['primary'];
    periwinkle?: Palette['primary'];
    jade?: Palette['primary'];
    port?: Palette['primary'];
    rose?: Palette['primary'];
    magenta?: Palette['primary'];
    orange?: Palette['primary'];
    terracotta?: Palette['primary'];
    blue?: Palette['primary'];
    cerulean?: Palette['primary'];
  }

  interface PaletteOptions {
    outline?: string;
    primaryShadowed?: PaletteOptions['primary'];
    secondaryShadowed?: PaletteOptions['primary'];
    errorShadowed?: PaletteOptions['primary'];
    warningShadowed?: PaletteOptions['primary'];
    successShadowed?: PaletteOptions['primary'];
    infoShadowed?: PaletteOptions['primary'];
    // Custom chip colors
    inProgress?: PaletteOptions['primary'];
    indigo?: PaletteOptions['primary'];
    periwinkle?: PaletteOptions['primary'];
    jade?: PaletteOptions['primary'];
    port?: PaletteOptions['primary'];
    rose?: PaletteOptions['primary'];
    magenta?: PaletteOptions['primary'];
    orange?: PaletteOptions['primary'];
    terracotta?: PaletteOptions['primary'];
    blue?: PaletteOptions['primary'];
    cerulean?: PaletteOptions['primary'];
  }
}

const black = capitalDesignTokens.foundations.colors.black;
const white = capitalDesignTokens.foundations.colors.white;

export const lightPalette: PaletteOptions = {
  mode: 'light',
  divider: alpha(black, 0.12),
  // 3.03:1 on paper (white), 3.01:1 on the default background (gray50). `divider` is 1.32:1.
  outline: alpha(black, 0.42),
  text: {
    primary: alpha(black, 0.87),
    secondary: alpha(black, 0.72),
    disabled: alpha(black, 0.58)
  },
  primary: {
    main: capitalDesignTokens.foundations.colors.blurple700,
    dark: capitalDesignTokens.foundations.colors.blurple900,
    light: capitalDesignTokens.foundations.colors.blurple100,
    contrastText: white
  },
  secondary: {
    main: capitalDesignTokens.foundations.colors.slate700,
    dark: capitalDesignTokens.foundations.colors.slate900,
    light: capitalDesignTokens.foundations.colors.slate100,
    contrastText: white
  },
  action: {
    active: alpha(black, 0.56),
    hover: alpha(black, 0.04),
    selected: alpha(black, 0.08),
    focus: alpha(black, 0.12),
    disabled: alpha(black, 0.38),
    disabledBackground: alpha(black, 0.12)
  },
  error: {
    main: capitalDesignTokens.foundations.colors.red700,
    dark: capitalDesignTokens.foundations.colors.red900,
    light: capitalDesignTokens.foundations.colors.red50,
    contrastText: white
  },
  warning: {
    main: capitalDesignTokens.foundations.colors.yellow700,
    dark: capitalDesignTokens.foundations.colors.yellow900,
    light: capitalDesignTokens.foundations.colors.yellow50,
    contrastText: white
  },
  success: {
    main: capitalDesignTokens.foundations.colors.green700,
    dark: capitalDesignTokens.foundations.colors.green900,
    light: capitalDesignTokens.foundations.colors.green50,
    contrastText: white
  },
  info: {
    main: capitalDesignTokens.foundations.colors.cerulean700,
    dark: capitalDesignTokens.foundations.colors.cerulean900,
    light: capitalDesignTokens.foundations.colors.cerulean50,
    contrastText: white
  },
  common: {
    black,
    white
  },
  background: {
    default: capitalDesignTokens.foundations.colors.gray50,
    paper: white,
    secondary: capitalDesignTokens.foundations.colors.gray50
  },
  grey: {
    50: capitalDesignTokens.foundations.colors.gray50,
    100: capitalDesignTokens.foundations.colors.gray100,
    200: capitalDesignTokens.foundations.colors.gray200,
    300: capitalDesignTokens.foundations.colors.gray300,
    400: capitalDesignTokens.foundations.colors.gray400,
    500: capitalDesignTokens.foundations.colors.gray500,
    600: capitalDesignTokens.foundations.colors.gray600,
    700: capitalDesignTokens.foundations.colors.gray700,
    800: capitalDesignTokens.foundations.colors.gray800,
    900: capitalDesignTokens.foundations.colors.gray900
  },
  primaryShadowed: {
    main: capitalDesignTokens.foundations.colors.blurple700
  },
  secondaryShadowed: {
    main: capitalDesignTokens.foundations.colors.gray700
  },
  errorShadowed: {
    main: capitalDesignTokens.foundations.colors.red700
  },
  warningShadowed: {
    main: capitalDesignTokens.foundations.colors.yellow700
  },
  successShadowed: {
    main: capitalDesignTokens.foundations.colors.green700
  },
  infoShadowed: {
    main: capitalDesignTokens.foundations.colors.blurple700
  },
  // Custom chip colors for MUI v5 compatibility
  inProgress: {
    main: capitalDesignTokens.foundations.colors.purple600,
    dark: capitalDesignTokens.foundations.colors.purple700,
    light: capitalDesignTokens.foundations.colors.purple100,
    contrastText: white
  },
  indigo: {
    main: capitalDesignTokens.foundations.colors.indigo600,
    dark: capitalDesignTokens.foundations.colors.indigo700,
    light: capitalDesignTokens.foundations.colors.indigo100,
    contrastText: white
  },
  periwinkle: {
    main: capitalDesignTokens.foundations.colors.periwinkle600,
    dark: capitalDesignTokens.foundations.colors.periwinkle700,
    light: capitalDesignTokens.foundations.colors.periwinkle100,
    contrastText: white
  },
  jade: {
    main: capitalDesignTokens.foundations.colors.jade600,
    dark: capitalDesignTokens.foundations.colors.jade700,
    light: capitalDesignTokens.foundations.colors.jade100,
    contrastText: white
  },
  port: {
    main: capitalDesignTokens.foundations.colors.port600,
    dark: capitalDesignTokens.foundations.colors.port700,
    light: capitalDesignTokens.foundations.colors.port100,
    contrastText: white
  },
  rose: {
    main: capitalDesignTokens.foundations.colors.rose600,
    dark: capitalDesignTokens.foundations.colors.rose700,
    light: capitalDesignTokens.foundations.colors.rose100,
    contrastText: white
  },
  magenta: {
    main: capitalDesignTokens.foundations.colors.magenta600,
    dark: capitalDesignTokens.foundations.colors.magenta700,
    light: capitalDesignTokens.foundations.colors.magenta100,
    contrastText: white
  },
  orange: {
    main: capitalDesignTokens.foundations.colors.orange600,
    dark: capitalDesignTokens.foundations.colors.orange700,
    light: capitalDesignTokens.foundations.colors.orange100,
    contrastText: white
  },
  terracotta: {
    main: capitalDesignTokens.foundations.colors.terracotta600,
    dark: capitalDesignTokens.foundations.colors.terracotta700,
    light: capitalDesignTokens.foundations.colors.terracotta100,
    contrastText: white
  },
  blue: {
    main: capitalDesignTokens.foundations.colors.blue600,
    dark: capitalDesignTokens.foundations.colors.blue700,
    light: capitalDesignTokens.foundations.colors.blue100,
    contrastText: white
  },
  cerulean: {
    main: capitalDesignTokens.foundations.colors.cerulean600,
    dark: capitalDesignTokens.foundations.colors.cerulean700,
    light: capitalDesignTokens.foundations.colors.cerulean100,
    contrastText: white
  }
};
